// adminBurn.mjs — /admin/burn 的判断层（第九十九批，SB-104 燃耗视图）。
//
// ── 这一页存在的理由 ────────────────────────────────────────────────────────
// hourly 修正案把本系统定为**计费台账正本**（不入系统的工时不可计费），对账的
// 人从 8/17 起每周要回答三个问题：谁这周记了多少净额？离周额线（60→40h）还有
// 多远？有没有单日 9~10h 的异常节奏？——这三问此前只能查库。
//
// ── 为什么区间在客户端算 ────────────────────────────────────────────────────
// 服务端刻意不做默认区间（routes_admin 头注）：/api/me 的默认区间锚定账号自己
// 时区的「今天」，而**对账的「本周」锚定的是查看者的日历**。这里用查看者本地
// 日期算周一，把「谁的今天」这个问题从服务端拿走。
//
// 纯函数、无 DOM；逐条单测在 adminBurn.test.mjs。

// 本地 Date → YYYYMMDD 数字标签。
export function ymdOf(date) {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

// YYYYMMDD → 本周周一（ISO：周一开头，周日归上一个周一——Upwork 计费周口径）。
// JS Date.getDay() 以 0=周日计，转 ISO 后做日移；用 Date 做日历算术免去闰年手算。
export function mondayOf(ymd) {
  const y = Math.floor(ymd / 10000), m = Math.floor((ymd % 10000) / 100) - 1, d = ymd % 100;
  const dt = new Date(y, m, d);
  const isoDow = (dt.getDay() + 6) % 7;          // 0=周一 … 6=周日
  dt.setDate(dt.getDate() - isoDow);
  return ymdOf(dt);
}

export function addDays(ymd, delta) {
  const y = Math.floor(ymd / 10000), m = Math.floor((ymd % 10000) / 100) - 1, d = ymd % 100;
  const dt = new Date(y, m, d);
  dt.setDate(dt.getDate() + delta);
  return ymdOf(dt);
}

// 默认对账区间：上周周一 → 本周周日（两个完整计费周）。
export function burnRange(todayYmd) {
  const thisMonday = mondayOf(todayYmd);
  return { fromDay: addDays(thisMonday, -7), toDay: addDays(thisMonday, 6) };
}

// demo 周额线（修正案一的日历，写死两段即可——它随 demo 结束一起过期，见
// SB-104 处理记录；把两个数做成配置面才是过度设计）。返回分钟；无线时 null。
export function weekCapMinutes(weekStart) {
  if (weekStart < 20260810) return null;
  if (weekStart < 20260817) return 60 * 60;      // 开工周：60h
  if (weekStart <= 20260928) return 40 * 60;     // 8/17 起：40h（demo 尾段沿用）
  return null;
}

// 分钟 → 显示小时（一位小数，整数不带小数点）："90 → 1.5"、"120 → 2"。
export function fmtHours(minutes) {
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1);
}

// YYYYMMDD → "8/11" 短显示（表格里用，年份由区间标题携带）。
export function fmtDayShort(ymd) {
  return `${Math.floor((ymd % 10000) / 100)}/${ymd % 100}`;
}

// 账号行 → 展示行：本周/上周净额 + 占额度比 + 最重的一天。
// weeks 由服务端按周降序回吐；这里按「本周周一」对位取值，不猜索引——
// 一个只在上周记过工时的账号，weeks[0] 是上周而不是本周。
export function displayRows(accounts, todayYmd) {
  const thisMonday = mondayOf(todayYmd);
  const lastMonday = addDays(thisMonday, -7);
  return (accounts ?? []).map((a) => {
    const weekOf = (start) => (a.weeks ?? []).find((w) => w.weekStart === start) ?? null;
    const cur = weekOf(thisMonday), prev = weekOf(lastMonday);
    let heaviest = null;
    for (const d of a.days ?? []) {
      if (!heaviest || d.netMinutes > heaviest.netMinutes) heaviest = d;
    }
    return {
      accountId: a.accountId,
      email: a.email || '',
      displayName: a.displayName || '',
      curMinutes: cur?.netMinutes ?? 0,
      curCapMinutes: weekCapMinutes(thisMonday),
      prevMinutes: prev?.netMinutes ?? 0,
      prevCapMinutes: weekCapMinutes(lastMonday),
      totalMinutes: a.totalNetMinutes ?? 0,
      heaviestDay: heaviest,          // {day, netMinutes} | null
      days: a.days ?? [],
    };
  });
}
